import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Divider
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';

/**
 * Dialog component for configuring table filters
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {Array} props.columns - Available columns that can be filtered
 * @param {Array} props.filters - Current active filters
 * @param {function} props.onApplyFilters - Function to call when filters should be applied
 */
const TableFilterDialog = ({
  open,
  onClose,
  columns = [],
  filters = [],
  onApplyFilters
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Local state to manage filters being configured
  const [localFilters, setLocalFilters] = React.useState([...filters]);
  
  // Reset local filters when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalFilters([...filters]);
    }
  }, [open, filters]);
  
  // Add a new empty filter
  const handleAddFilter = () => {
    setLocalFilters([
      ...localFilters,
      { id: Date.now(), column: '', operator: 'contains', value: '' }
    ]);
  };
  
  // Remove a filter by id
  const handleRemoveFilter = (filterId) => {
    setLocalFilters(localFilters.filter(filter => filter.id !== filterId));
  };
  
  // Update a specific filter
  const handleFilterChange = (filterId, field, value) => {
    setLocalFilters(localFilters.map(filter => 
      filter.id === filterId ? { ...filter, [field]: value } : filter
    ));
  };
  
  // Apply filters and close dialog
  const handleApply = () => {
    // Filter out incomplete filters
    const validFilters = localFilters.filter(
      filter => filter.column && filter.operator && filter.value
    );
    
    if (onApplyFilters) {
      onApplyFilters(validFilters);
    }
    onClose();
  };
  
  // Clear all filters
  const handleClearAll = () => {
    setLocalFilters([]);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        elevation: 3,
        sx: { 
          borderRadius: '8px',
          minWidth: '500px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        pb: 1 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filter Options</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ py: 2 }}>
        {localFilters.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No filters added. Click "Add Filter" to create one.
          </Typography>
        ) : (
          localFilters.map((filter, index) => (
            <Box 
              key={filter.id} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                gap: 1
              }}
            >
              {/* Column selector */}
              <FormControl size="small" sx={{ minWidth: '150px' }}>
                <InputLabel>Column</InputLabel>
                <Select
                  value={filter.column}
                  onChange={(e) => handleFilterChange(filter.id, 'column', e.target.value)}
                  label="Column"
                >
                  {columns.map(column => (
                    <MenuItem key={column.id} value={column.id}>
                      {column.header || column.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Operator selector */}
              <FormControl size="small" sx={{ minWidth: '120px' }}>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={filter.operator}
                  onChange={(e) => handleFilterChange(filter.id, 'operator', e.target.value)}
                  label="Operator"
                >
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="equals">Equals</MenuItem>
                  <MenuItem value="startsWith">Starts with</MenuItem>
                  <MenuItem value="endsWith">Ends with</MenuItem>
                  <MenuItem value="greaterThan">Greater than</MenuItem>
                  <MenuItem value="lessThan">Less than</MenuItem>
                </Select>
              </FormControl>
              
              {/* Value input */}
              <TextField
                size="small"
                label="Value"
                value={filter.value}
                onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                sx={{ flex: 1 }}
              />
              
              {/* Remove button */}
              <IconButton 
                onClick={() => handleRemoveFilter(filter.id)} 
                color="error"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
        
        <Button 
          variant="outlined" 
          onClick={handleAddFilter}
          startIcon={<FilterListIcon />}
          sx={{ mt: 1 }}
        >
          Add Filter
        </Button>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClearAll}
          disabled={localFilters.length === 0}
          sx={{ color: colors.grey[500] }}
        >
          Clear All
        </Button>
        <Button 
          onClick={onClose}
          sx={{ color: colors.grey[700] }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleApply}
          variant="contained"
          disabled={localFilters.length === 0}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TableFilterDialog;
